import type { CurrencyRow } from './rows.js';

/**
 * Where each currency comes in a list, as the Settings page orders them.
 *
 * Returned as a rank to sort by rather than a sorted list, because what gets
 * sorted is never the currencies themselves — it is a breakdown's columns, a
 * cell's chart lines, keyed by code. A code the book does not know (a leg in
 * a currency since deleted, or not yet synced) sorts after every known one,
 * alphabetically among the strays, rather than throwing or jumping to the
 * front.
 *
 * Unordered currencies (`sortOrder` null) count as 0 and fall back to their
 * code, which is the alphabetical order every list had before there was a
 * choice.
 */
export function currencyRank(currencies: readonly Pick<CurrencyRow, 'code' | 'sortOrder'>[]): (code: string) => number {
  const ordered = [...currencies].sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.code.localeCompare(b.code),
  );
  const rank = new Map(ordered.map((c, i) => [c.code, i] as const));
  return (code) => rank.get(code) ?? ordered.length;
}

/** Sort codes by `rank`, then by code — so strays, which share a rank, are alphabetical. */
export function byCurrencyRank(rank: (code: string) => number): (a: string, b: string) => number {
  return (a, b) => rank(a) - rank(b) || a.localeCompare(b);
}
